import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Card, 
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Bolt as BoltIcon,
  Shield as ShieldIcon,
  Link as LinkIcon,
  Analytics as AnalyticsIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  Checklist as ChecklistIcon,
  MenuBook as MenuBookIcon,
  Category as CategoryIcon,
  Timeline as TimelineIcon,
  Policy as PolicyIcon,
  SwapHoriz as SwapHorizIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const BenefitsPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: <BoltIcon fontSize="large" color="primary" />,
      title: "Real-Time Data Access",
      description: "Work with trusted, up-to-date information across all systems and domains"
    },
    {
      icon: <ShieldIcon fontSize="large" color="primary" />,
      title: "Built-In Governance",
      description: "Minimize regulatory risks with automated compliance controls and audit trails"
    },
    {
      icon: <LinkIcon fontSize="large" color="primary" />,
      title: "Seamless Integration",
      description: "Connect any system in minutes with our robust API ecosystem"
    },
    {
      icon: <AnalyticsIcon fontSize="large" color="primary" />,
      title: "Self-Service Analytics",
      description: "Reduce time-to-insight with intuitive data exploration tools"
    },
    {
      icon: <GroupsIcon fontSize="large" color="primary" />,
      title: "Cross-Agency Collaboration",
      description: "Break down silos with shared data products and workflows"
    },
    {
      icon: <ChecklistIcon fontSize="large" color="primary" />,
      title: "Standardized Management",
      description: "Enterprise-grade controls for consistent data practices"
    }
  ];

  const govFeatures = [
    "FISMA-compliant metadata management",
    "NIST 800-53 policy templates",
    "ATO documentation automation",
    "Section 508 compliant UI",
    "FedRAMP-authorized connectors",
    "DISA STIG compliance"
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Hero Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6, px: 4, borderRadius: 2, mb: 4 }}>
        <Typography variant="h2" align="center" sx={{ fontWeight: 700, color: 'white', mb: 2 }}>
          Platform Benefits
        </Typography>
        <Typography variant="h6" align="center" sx={{ color: 'rgba(255,255,255,0.9)' }}>
          The foundation for intelligent, secure data strategy across your enterprise
        </Typography>
      </Box>

      {/* Main Content */}
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: 3, p: 4 }}>
          <Container maxWidth="md">
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 700, mb: 3 }}>
              Key Benefits
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
              E-Unify delivers comprehensive data intelligence capabilities designed specifically for federal agencies. 
              Our platform enables real-time collaboration, governance, and insights while maintaining the highest 
              standards of security and compliance.
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card 
                    sx={{ 
                      p: 3, 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: 6
                      }
                    }}
                    onClick={() => navigate(`/benefits/${benefit.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/benefits/${benefit.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`);
                      }
                    }}
                    aria-label={`View details about ${benefit.title}`}
                  >
                    <Box sx={{ mb: 2 }}>
                      {benefit.icon}
                    </Box>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {benefit.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {benefit.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Core Features Section */}
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 700, mt: 6, mb: 3 }}>
              Core Features
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Business Glossary */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderLeft: '4px solid #003366', 
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/features/business-glossary')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MenuBookIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Business Glossary
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Standardized terminology and definitions for business concepts across the organization, ensuring consistent understanding of key terms.
                  </Typography>
                </Card>
              </Grid>

              {/* Data Catalog */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderLeft: '4px solid #003366', 
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/features/data-catalog-detail')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CategoryIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Data Catalog
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Comprehensive inventory of data assets with detailed metadata, making data discoverable and understandable for all users.
                  </Typography>
                </Card>
              </Grid>

              {/* Data Lineage */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderLeft: '4px solid #003366', 
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/features/data-lineage')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TimelineIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Data Lineage
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Visual tracking of data flow from source to consumption, providing transparency into transformations and dependencies.
                  </Typography>
                </Card>
              </Grid>

              {/* Policy Management */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderLeft: '4px solid #003366', 
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/features/policy-management')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PolicyIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Policy Management
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Documentation and enforcement of data governance policies, standards, and procedures across the enterprise.
                  </Typography>
                </Card>
              </Grid>

              {/* Workflow Automation */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderLeft: '4px solid #003366', 
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/features/workflow-automation')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SwapHorizIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Workflow Automation
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Streamlined processes for metadata curation, approvals, and change management to ensure data quality.
                  </Typography>
                </Card>
              </Grid>

              {/* Impact Analysis */}
              <Grid item xs={12} md={6}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    borderLeft: '4px solid #003366', 
                    p: 3,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => navigate('/features/impact-analysis')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AssignmentIcon sx={{ color: 'primary.main', mr: 1, fontSize: 28 }} />
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      Impact Analysis
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Assessment capabilities to understand the downstream effects of proposed data changes before implementation.
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* Government Features - Matching About page style */}
            <Paper sx={{ p: 4, bgcolor: '#f5f5f5', mt: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                Government-Grade Features
              </Typography>
              <List>
                {govFeatures.map((feature, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Container>
        </Paper>
      </Box>

      {/* CTA Section */}
      <Paper sx={{ bgcolor: 'primary.main', color: 'white', py: 6, px: 4, borderRadius: 2, mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
            Ready to Unify Your Data Strategy?
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', color: 'rgba(255,255,255,0.9)' }}>
            Discover how E-Unify can support your agency's data intelligence needs
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: '#f8f9fa'
                }
              }}
            >
              Contact Us
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/register')}
              sx={{
                borderColor: 'white',
                borderWidth: 2,
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'white',
                  borderWidth: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.15)'
                }
              }}
            >
              Get Started
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default BenefitsPage;
